export type UnitSystem = 'metric' | 'imperial'

// Conversion constants
const KG_TO_LBS = 2.20462

/**
 * Convert weight from kg to the specified unit system
 */
export const convertWeight = (weightKg: number, toUnit: UnitSystem): number => {
  if (toUnit === 'imperial') {
    return weightKg * KG_TO_LBS
  }
  return weightKg
}

/**
 * Convert weight from specified unit system to kg
 */
export const convertWeightToKg = (weight: number, fromUnit: UnitSystem): number => {
  if (fromUnit === 'imperial') {
    return weight / KG_TO_LBS
  }
  return weight
}

/**
 * Format weight value with appropriate unit label and rounding
 */
export const formatWeight = (weightKg: number, unitSystem: UnitSystem, roundTo = 1): string => {
  const convertedWeight = convertWeight(weightKg, unitSystem)
  const roundedWeight = Math.round(convertedWeight * 10) / 10 // Round to 1 decimal place
  const unit = unitSystem === 'imperial' ? 'lbs' : 'kg'

  return `${roundedWeight.toFixed(roundTo)} ${unit}`
}

/**
 * Format weight change with appropriate unit and sign
 */
export const formatWeightChange = (changeKg: number, unitSystem: UnitSystem): string => {
  const convertedChange = convertWeight(changeKg, unitSystem)
  const roundedChange = Math.round(convertedChange * 100) / 100 // Keep 2 decimals for change rates
  const unit = unitSystem === 'imperial' ? 'lbs' : 'kg'
  const sign = roundedChange >= 0 ? '+' : ''

  return `${sign}${roundedChange.toFixed(2)} ${unit}/week`
}

/**
 * Get the unit label for the current system
 */
export const getWeightUnit = (unitSystem: UnitSystem): string => {
  return unitSystem === 'imperial' ? 'lbs' : 'kg'
}

/**
 * Format just the weight value (no unit) with proper rounding
 */
export const formatWeightValue = (weightKg: number, unitSystem: UnitSystem): string => {
  const convertedWeight = convertWeight(weightKg, unitSystem)
  const roundedWeight = Math.round(convertedWeight * 10) / 10 // Round to 1 decimal place

  return roundedWeight.toFixed(1)
}

/**
 * Format exercise weight display - shows "Bodyweight" for bodyweight exercises with 0kg or null
 */
export const formatExerciseWeight = (
  weightKg: number | null,
  isBodyweight: boolean,
  unitSystem: UnitSystem = 'metric'
): string => {
  // Handle Hevy API null values for bodyweight exercises
  if ((isBodyweight && (weightKg === 0 || weightKg === null)) || weightKg === null) {
    return 'Bodyweight'
  }
  return formatWeight(weightKg || 0, unitSystem)
}

/**
 * Format exercise weight value only (no unit) - shows "BW" for bodyweight exercises with 0kg or null
 */
export const formatExerciseWeightValue = (
  weightKg: number | null,
  isBodyweight: boolean,
  unitSystem: UnitSystem = 'metric'
): string => {
  // Handle Hevy API null values for bodyweight exercises
  if ((isBodyweight && (weightKg === 0 || weightKg === null)) || weightKg === null) {
    return 'BW'
  }
  return formatWeightValue(weightKg || 0, unitSystem)
}
